import React, { useEffect, useState, createElement } from 'react';
import { X, Eye, EyeOff, RefreshCw, Copy, Check, AlertTriangle, Shield } from 'lucide-react';
import { Button } from './ui/Button';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from './ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getEmployees } from '../utils/companyManagement';
// Generate a readable temporary password
function generateTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  for (let i = 0; i < 8; i++) {
    password += chars[array[i] % chars.length];
  }
  return password;
}
// Fallback copy that works in all contexts (iframes, non-HTTPS, etc.)
function copyToClipboard(text: string): Promise<void> {
  // Try modern API first
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback: textarea + execCommand
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) resolve();else reject(new Error('execCommand copy failed'));
    } catch (err) {
      document.body.removeChild(textarea);
      reject(err);
    }
  });
}
interface EmployeeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employee: any) => void;
  editingEmployee?: any;
  lockedCompanyId?: number;
  managerMode?: boolean;
}
export function EmployeeFormModal({
  isOpen,
  onClose,
  onSave,
  editingEmployee,
  lockedCompanyId,
  managerMode = false
}: EmployeeFormModalProps) {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    position: '',
    companyId: '' as string | number,
    branchId: '',
    status: 'Active',
    permissions: {
      canOrder: true,
      canViewReports: false,
      canManageMenu: false,
      canManageEmployees: false,
      isManager: false,
      tempPassword: false
    }
  });
  const [availableBranches, setAvailableBranches] = useState<any[]>([]);
  const [existingUsernames, setExistingUsernames] = useState<string[]>([]);
  const [usernameError, setUsernameError] = useState('');
  // Helper: get branches from the already-loaded companies state (same data source)
  const getBranchesFromCompanies = (companyId: string | number) => {
    const company = companies.find(c => String(c.id) === String(companyId));
    return company?.branches || [];
  };
  // Load existing usernames for uniqueness validation
  useEffect(() => {
    const loadUsernames = async () => {
      try {
        const employees = await getEmployees();
        const usernames = employees.filter((e: any) => e.username).map((e: any) => e.username.toLowerCase());
        setExistingUsernames(usernames);
      } catch {
        // Silently fail - validation will just be skipped
      }
    };
    if (isOpen) loadUsernames();
  }, [isOpen]);
  // Validate username uniqueness
  const validateUsername = (username: string) => {
    if (!username) {
      setUsernameError('');
      return true;
    }
    const normalizedUsername = username.toLowerCase().trim();
    const isDuplicate = existingUsernames.some(existing => existing === normalizedUsername && (!editingEmployee || editingEmployee.username?.toLowerCase() !== normalizedUsername));
    if (isDuplicate) {
      setUsernameError('Este nombre de usuario ya existe. Elige otro.');
      return false;
    }
    setUsernameError('');
    return true;
  };
  const handleGeneratePassword = () => {
    const tempPass = generateTempPassword();
    setFormData(prev => ({
      ...prev,
      password: tempPass,
      permissions: {
        ...prev.permissions,
        tempPassword: true
      }
    }));
    setShowPassword(true);
    setCopied(false);
  };
  const handleCopyPassword = async () => {
    try {
      await copyToClipboard(formData.password);
      setCopied(true);
      showToast('success', 'Copiado', 'Contraseña copiada al portapapeles');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Last resort: show password so user can manually copy
      setShowPassword(true);
      showToast('warning', 'Copia manual', 'Selecciona y copia la contraseña manualmente');
    }
  };
  // Load companies from Supabase
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoading(true);
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data);
          if (data.length > 0 && !editingEmployee) {
            setFormData(prev => ({
              ...prev,
              companyId: lockedCompanyId || data[0].id
            }));
          }
        } else {
          const saved = localStorage.getItem('criket_companies');
          const localCompanies = saved ? JSON.parse(saved) : [];
          setCompanies(localCompanies);
          if (localCompanies.length > 0 && !editingEmployee) {
            setFormData(prev => ({
              ...prev,
              companyId: lockedCompanyId || localCompanies[0].id
            }));
          }
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);
  // Update form when modal opens or editingEmployee changes
  useEffect(() => {
    if (!isOpen || companies.length === 0) return;
    setCopied(false);
    if (editingEmployee) {
      const companyId = editingEmployee.companyId || editingEmployee.company_id || lockedCompanyId || companies[0]?.id || '';
      const branchId = editingEmployee.branchId || editingEmployee.branch_id || '';
      setFormData({
        name: editingEmployee.name || '',
        email: editingEmployee.email || '',
        username: editingEmployee.username || '',
        password: '',
        position: editingEmployee.position || '',
        companyId: companyId,
        branchId: branchId,
        status: editingEmployee.status || 'Active',
        permissions: {
          canOrder: editingEmployee.permissions?.canOrder ?? true,
          canViewReports: editingEmployee.permissions?.canViewReports ?? false,
          canManageMenu: editingEmployee.permissions?.canManageMenu ?? false,
          canManageEmployees: editingEmployee.permissions?.canManageEmployees ?? false,
          isManager: editingEmployee.permissions?.isManager ?? false,
          tempPassword: false
        }
      });
    } else {
      // New employee - auto-generate temp password
      const initialCompanyId = lockedCompanyId || companies[0]?.id || '';
      const branches = getBranchesFromCompanies(initialCompanyId);
      const tempPass = generateTempPassword();
      setFormData({
        name: '',
        email: '',
        username: '',
        password: tempPass,
        position: '',
        companyId: initialCompanyId,
        branchId: branches[0]?.id || '',
        status: 'Active',
        permissions: {
          canOrder: true,
          canViewReports: false,
          canManageMenu: false,
          canManageEmployees: false,
          isManager: false,
          tempPassword: true
        }
      });
      setShowPassword(true);
    }
  }, [isOpen, editingEmployee?.id, companies]);
  // Update available branches when company changes
  useEffect(() => {
    const branches = getBranchesFromCompanies(formData.companyId);
    setAvailableBranches(branches);
    const currentBranchExists = branches.some((b: any) => b.id === formData.branchId);
    if (branches.length > 0 && !currentBranchExists) {
      setFormData(prev => ({
        ...prev,
        branchId: branches[0].id
      }));
    }
  }, [formData.companyId, companies]);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In manager mode, auto-generate username if empty
    let finalUsername = formData.username;
    if (managerMode && !finalUsername && !editingEmployee) {
      finalUsername = formData.name.toLowerCase().replace(/\s+/g, '.').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    if (!managerMode && !finalUsername) {
      showToast('warning', 'Campo requerido', 'El nombre de usuario es requerido');
      return;
    }
    // Validate username uniqueness
    if (finalUsername && !validateUsername(finalUsername)) {
      showToast('error', 'Usuario duplicado', 'Este nombre de usuario ya está en uso. Por favor elige otro.');
      return;
    }
    // In manager mode for new employees, auto-generate password if empty
    let finalPassword = formData.password;
    if (managerMode && !finalPassword && !editingEmployee) {
      finalPassword = generateTempPassword();
    }
    if (!editingEmployee && !finalPassword) {
      showToast('warning', 'Campo requerido', 'La contraseña es requerida para nuevos empleados');
      return;
    }
    const company = companies.find(c => c.id === formData.companyId);
    const branch = availableBranches.find(b => b.id === formData.branchId);
    const employeeData = {
      ...formData,
      username: finalUsername,
      password: finalPassword,
      company: company?.name || '',
      branch: branch?.name || '',
      permissions: {
        ...formData.permissions,
        tempPassword: !editingEmployee ? true : formData.permissions.tempPassword
      }
    };
    onSave(employeeData);
    onClose();
  };
  if (loading) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-277">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 p-8" data-id="element-278">
          <div className="text-center" data-id="element-279">
            <div className="w-12 h-12 border-2 border-gray-200 dark:border-gray-700 border-t-gray-900 dark:border-t-white rounded-full animate-spin mx-auto mb-4" data-id="element-280"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-281">
              Cargando empresas...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-282">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" data-id="element-283">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900" data-id="element-284">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-285">
            {editingEmployee ? 'Editar' : 'Nuevo'} Empleado
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-286">
            <X size={20} data-id="element-287" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-288">
          <div data-id="element-289">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-290">
              Nombre Completo
            </label>
            <input type="text" required value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Juan Pérez" data-id="element-291" />
          </div>

          <div data-id="element-292">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-293">
              Correo Electrónico
            </label>
            <input type="email" required value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="juan@empresa.com" data-id="element-294" />
          </div>

          {!managerMode && <div data-id="element-295">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-296">
                Nombre de Usuario
              </label>
              <input type="text" required value={formData.username} onChange={e => {
            const val = e.target.value;
            setFormData({
              ...formData,
              username: val
            });
            validateUsername(val);
          }} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${usernameError ? 'border-red-400 dark:border-red-600 focus:ring-red-500' : 'border-gray-300 dark:border-gray-700 focus:ring-black dark:focus:ring-white'}`} placeholder="juanperez" data-id="element-297" />
              {usernameError && <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-id="element-298">
                  <AlertTriangle size={12} data-id="element-299" />
                  {usernameError}
                </p>}
            </div>}

          {!managerMode && <div data-id="element-300">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-301">
                Contraseña{' '}
                {editingEmployee && '(dejar en blanco para mantener)'}
              </label>
              <div className="relative" data-id="element-302">
                <input type={showPassword ? 'text' : 'password'} required={!editingEmployee} value={formData.password} onChange={e => {
              setFormData({
                ...formData,
                password: e.target.value,
                // If manually typing, it's not a temp password
                permissions: {
                  ...formData.permissions,
                  tempPassword: false
                }
              });
            }} className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono" placeholder="••••••••" data-id="element-303" />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5" data-id="element-304">
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-305">
                    {showPassword ? <EyeOff size={16} data-id="element-306" /> : <Eye size={16} data-id="element-307" />}
                  </button>
                </div>
              </div>

              {/* Temp password actions */}
              <div className="flex items-center gap-2 mt-2" data-id="element-308">
                <button type="button" onClick={handleGeneratePassword} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" data-id="element-309">
                  <RefreshCw size={12} data-id="element-310" />
                  Generar temporal
                </button>
                {formData.password && <button type="button" onClick={handleCopyPassword} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${copied ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' : 'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'}`} data-id="element-311">
                    {copied ? <Check size={12} data-id="element-312" /> : <Copy size={12} data-id="element-313" />}
                    {copied ? 'Copiada' : 'Copiar'}
                  </button>}
              </div>

              {/* Temp password notice */}
              {formData.permissions.tempPassword && formData.password && !editingEmployee && <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-id="element-314">
                    <div className="flex items-start gap-2" data-id="element-315">
                      <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" data-id="element-316" />
                      <div data-id="element-317">
                        <p className="text-xs font-bold text-amber-800 dark:text-amber-200" data-id="element-318">
                          Contraseña temporal
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5" data-id="element-319">
                          El empleado deberá cambiarla en su primer inicio de
                          sesión. Asegúrate de compartirle esta contraseña.
                        </p>
                      </div>
                    </div>
                  </div>}
            </div>}

          {/* Manager Mode: Show generated credentials for new employees */}
          {managerMode && !editingEmployee && <div className="border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl p-4 space-y-3" data-id="element-320">
              <div className="flex items-center gap-2 mb-1" data-id="element-321">
                <Shield size={16} className="text-blue-600 dark:text-blue-400" data-id="element-322" />
                <span className="text-sm font-bold text-blue-900 dark:text-blue-200" data-id="element-323">
                  Credenciales de Acceso
                </span>
              </div>

              <div data-id="element-324">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1" data-id="element-325">
                  Usuario (se genera del nombre)
                </label>
                <div className="flex items-center gap-2" data-id="element-326">
                  <div className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-mono text-gray-900 dark:text-white" data-id="element-327">
                    {formData.name ? formData.name.toLowerCase().replace(/\s+/g, '.').normalize('NFD').replace(/[\u0300-\u036f]/g, '') : <span className="text-gray-400 italic" data-id="element-328">
                        Escribe el nombre arriba...
                      </span>}
                  </div>
                  {formData.name && <button type="button" onClick={async () => {
                const username = formData.name.toLowerCase().replace(/\s+/g, '.').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                try {
                  await copyToClipboard(username);
                  showToast('success', 'Copiado', 'Usuario copiado');
                } catch {
                  showToast('warning', 'Copia manual', 'Selecciona y copia manualmente');
                }
              }} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" title="Copiar usuario" data-id="element-329">
                      <Copy size={16} data-id="element-330" />
                    </button>}
                </div>
                {formData.name && existingUsernames.includes(formData.name.toLowerCase().replace(/\s+/g, '.').normalize('NFD').replace(/[\u0300-\u036f]/g, '')) && <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-id="element-331">
                      <AlertTriangle size={12} data-id="element-332" />
                      Este usuario ya existe. Cambia el nombre o edita
                      manualmente.
                    </p>}
              </div>

              <div data-id="element-333">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1" data-id="element-334">
                  Contraseña temporal
                </label>
                <div className="flex items-center gap-2" data-id="element-335">
                  <div className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-mono text-gray-900 dark:text-white tracking-wide select-all" data-id="element-336">
                    {showPassword ? formData.password : '••••••••'}
                  </div>
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" title={showPassword ? 'Ocultar' : 'Mostrar'} data-id="element-337">
                    {showPassword ? <EyeOff size={16} data-id="element-338" /> : <Eye size={16} data-id="element-339" />}
                  </button>
                  <button type="button" onClick={handleCopyPassword} className={`p-2 border rounded-lg transition-colors ${copied ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`} title="Copiar contraseña" data-id="element-340">
                    {copied ? <Check size={16} data-id="element-341" /> : <Copy size={16} data-id="element-342" />}
                  </button>
                  <button type="button" onClick={handleGeneratePassword} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Regenerar contraseña" data-id="element-343">
                    <RefreshCw size={16} data-id="element-344" />
                  </button>
                </div>
              </div>

              <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg" data-id="element-345">
                <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" data-id="element-346" />
                <p className="text-xs text-amber-700 dark:text-amber-300" data-id="element-347">
                  <strong data-id="element-348">Importante:</strong> Comparte estas credenciales al
                  empleado. Deberá cambiar la contraseña en su primer inicio de
                  sesión.
                </p>
              </div>
            </div>}

          <div data-id="element-349">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-350">
              Cargo/Posición
            </label>
            <input type="text" required value={formData.position} onChange={e => setFormData({
            ...formData,
            position: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Desarrollador" data-id="element-351" />
          </div>

          <div data-id="element-352">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-353">
              Empresa
            </label>
            <select value={formData.companyId} onChange={e => {
            const selectedCompany = companies.find((c: any) => String(c.id) === e.target.value);
            setFormData({
              ...formData,
              companyId: selectedCompany?.id || e.target.value
            });
          }} disabled={!!lockedCompanyId} className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${lockedCompanyId ? 'opacity-70 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`} data-id="element-354">
              {companies.map(company => <option key={company.id} value={String(company.id)} data-id="element-355">
                  {company.name}
                </option>)}
            </select>
            {lockedCompanyId && <p className="text-xs text-gray-500 mt-1" data-id="element-356">
                Empresa asignada automáticamente
              </p>}
          </div>

          <div data-id="element-357">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-358">
              Sucursal ({availableBranches.length} disponibles)
            </label>
            <select value={formData.branchId} onChange={e => setFormData({
            ...formData,
            branchId: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" required data-id="element-359">
              {availableBranches.length === 0 ? <option value="" data-id="element-360">No hay sucursales disponibles</option> : availableBranches.map(branch => <option key={branch.id} value={branch.id} data-id="element-361">
                    {branch.name}
                  </option>)}
            </select>
          </div>

          {!managerMode && <div data-id="element-362">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-363">
                Estado
              </label>
              <select value={formData.status} onChange={e => setFormData({
            ...formData,
            status: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-364">
                <option value="Active" data-id="element-365">Activo</option>
                <option value="Inactive" data-id="element-366">Inactivo</option>
              </select>
            </div>}

          {!managerMode && <div className="border-t border-gray-200 dark:border-gray-800 pt-4" data-id="element-367">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-368">
                Permisos
              </label>
              <div className="space-y-2" data-id="element-369">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-370">
                  <input type="checkbox" checked={formData.permissions.canOrder} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canOrder: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-371" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-372">
                    Puede hacer pedidos
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer" data-id="element-373">
                  <input type="checkbox" checked={formData.permissions.canViewReports} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canViewReports: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-374" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-375">
                    Puede ver reportes
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer" data-id="element-376">
                  <input type="checkbox" checked={formData.permissions.canManageMenu} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageMenu: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-377" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-378">
                    Puede gestionar menú
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer" data-id="element-379">
                  <input type="checkbox" checked={formData.permissions.canManageEmployees} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  canManageEmployees: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-380" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-381">
                    Puede gestionar empleados
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer" data-id="element-382">
                  <input type="checkbox" checked={formData.permissions.isManager} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  isManager: e.target.checked
                }
              })} className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black" data-id="element-383" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-384">
                    Gerente (ver consumos de empresa)
                  </span>
                </label>
              </div>
            </div>}

          <div className="flex gap-3 pt-4" data-id="element-385">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-386">
              Cancelar
            </Button>
            <Button type="submit" fullWidth disabled={availableBranches.length === 0} data-id="element-387">
              {editingEmployee ? 'Actualizar' : 'Guardar'} Empleado
            </Button>
          </div>
        </form>
      </div>
    </div>;
}